import { useState } from 'react';

import { FormInputVariants } from '#/service/constants';

import InputCheckbox from './components/InputCheckbox';
import InputLink from './components/InputLink';
import InputSelect from './components/InputSelect';
import InputText from './components/InputText';
import {
  CommonInputProps,
  FormInputProps,
  InputHandlerProps,
} from './FormInput.types';

const FormInput = ({
  icon,
  dataKey,
  label,
  placeholder,
  required,
  state,
  updateErrorMessages,
  updateState,
  values = [],
  variant,
  help,
  linkPath,
  handleValidate,
  ...rest
}: FormInputProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const focusInput = () => {
    setIsFocused(true);
    updateErrorMessages?.(dataKey, undefined);
  };

  const blurInput = () => {
    setIsFocused(false);
    // casting as this is the only place where input state is used partially. We can't use value here as it would clear value on blur
    updateState(dataKey, { touched: true });
  };

  const mouseEnterInput = () => {
    setIsHovered(true);
  };

  const mouseLeaveInput = () => {
    setIsHovered(false);
  };

  const commonProps: CommonInputProps = {
    dataKey,
    label: label || placeholder || '',
    required,
    updateErrorMessages,
    updateInputValue: updateState,
    handleBlur: blurInput,
    touched: state?.touched,
  };

  const additionalHandlers: InputHandlerProps = {
    handleFocus: focusInput,
    handleMouseEnter: mouseEnterInput,
    handleMouseLeave: mouseLeaveInput,
  };

  if (
    variant === FormInputVariants.TEXT ||
    variant === FormInputVariants.EMAIL ||
    variant === FormInputVariants.PASSWORD ||
    variant === FormInputVariants.NUMBER
  ) {
    return (
      <InputText
        handleValidate={handleValidate}
        help={help}
        highlighted={isFocused || isHovered}
        icon={icon}
        inputValue={state?.value?.toString()}
        isFocused={isFocused}
        variant={variant}
        {...commonProps}
        {...additionalHandlers}
        {...rest}
      />
    );
  }

  if (variant === FormInputVariants.SELECT) {
    return (
      <InputSelect
        highlighted={isFocused || isHovered}
        icon={icon}
        inputValue={state?.value?.toString()}
        values={values}
        {...commonProps}
        {...additionalHandlers}
        {...rest}
      />
    );
  }

  if (variant === FormInputVariants.CHECKBOX) {
    return (
      <InputCheckbox inputValue={!!state.value} {...commonProps} {...rest} />
    );
  }

  if (variant === FormInputVariants.LINK) {
    return <InputLink label={label} path={linkPath} {...rest} />;
  }

  return null;
};

export default FormInput;
